import React, { useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import {
  getEmailSchedules,
  updateEmailSchedule,
  deleteEmailSchedule,
} from "../../redux/modules/emailSchedules";
import { useDispatch, useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material";
import { FormControl } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  convertToTimeZone,
  convertUTCToTimezone,
  formatDate,
} from "../../utils/format_date";
import { getBTemplates } from "../../redux/modules/email";
import CloseIcon from "@mui/icons-material/Close";
import { Typography } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";

const EmailSchedule = () => {
  const currentTZ = useSelector((state) => state.auth.timezone);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [count, setCount] = useState(0);
  const [editData, setEditData] = useState({
    schedule_time: new Date(),
    template_id: "",
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false); // Loader state

  const dispatch = useDispatch();
  const bodyTemplates = useSelector((state) => state.email.bodyTemplates);

  useEffect(() => {
    setLoading(true);
    dispatch(getEmailSchedules(page, limit, encodeURIComponent(searchText)))
      .then((res) => {
        setData(res?.schedules || []);
        setTotalCount(res?.count || 0);
      })
      .finally(() => setLoading(false));
  }, [page, limit, searchText, count]);

  const columns = [
    { name: "deal_name", label: "Deal Name" },
    {
      name: "stage_transition",
      label: "Stage Transition",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = data[tableMeta.rowIndex];
          return `${rowData.deal_stage_from_name || "-"} → ${
            rowData.deal_stage_to_name || "-"
          }`;
        },
      },
    },
    {
      name: "source_type",
      label: "Rule Type",
      options: {
        customBodyRender: (value) =>
          value === "pipeline_rule"
            ? "Pipeline Rule"
            : "Deal Specific Override",
      },
    },
    { name: "template_name", label: "Template" },
    {
      name: "sender_name",
      label: "Sender Name",
      options: {
        customBodyRender: (value) => {
          const sender_name = value !== null ? value : "-----";
          return sender_name;
        },
      },
    },
    {
      name: "tagged_contacts",
      label: "Contacts",
      options: {
        customBodyRender: (value) => {
          const contacts =
            value?.length > 0 ? value?.join(", ") : "No contacts found";
          return contacts;
        },
        setCellProps: () => ({ style: { width: "250px" } }),
      },
    },
    {
      name: "schedule_time",
      label: "Send Time",
      options: {
        customBodyRender: (value) => convertUTCToTimezone(value, currentTZ),
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        customBodyRender: (value) => (
          <Chip
            size="small"
            label={
              value === "1"
                ? "Sent"
                : value === "0"
                ? "Pending"
                : value === "2"
                ? "Failed"
                : value === "4"
                ? "Overridden"
                : "Discarded"
            }
            color={
              value === "1"
                ? "success"
                : value === "0"
                ? "info"
                : value === "2"
                ? "error"
                : value === "4"
                ? "secondary"
                : "warning"
            }
          />
        ),
      },
    },
    {
      name: "id",
      label: "Action",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const rowData = data[dataIndex];
          return (
            <>
              <IconButton size="small" onClick={() => handleDelete(rowData.id)}>
                <DeleteIcon fontSize="inherit" />
              </IconButton>
              {rowData.status === "0" && (
                <IconButton size="small" onClick={() => handleEdit(rowData.id)}>
                  <EditIcon fontSize="inherit" />
                </IconButton>
              )}
            </>
          );
        },
      },
    },
  ];

  const onRefresh = () => {
    setCount(count + 1);
  };

  const options = {
    filter: false,
    selectableRows: "none",
    responsive: "standard",
    download: false,
    print: false,
    serverSide: true,
    count: totalCount,
    page: page - 1,
    rowsPerPage: limit,
    customToolbar: () => {
      return (
        <IconButton onClick={onRefresh}>
          <RefreshIcon />
        </IconButton>
      );
    },
    onSearchChange: (searchText) => {
      setPage(1);
      setSearchText(searchText);
    },
    onTableChange: (action, tableState) => {
      if (action === "changePage") {
        setPage(tableState.page + 1);
      }
      if (action === "changeRowsPerPage") {
        setLimit(tableState.rowsPerPage);
        setPage(1);
      }
    },

    textLabels: {
      body: {
        noMatch: loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 3,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          "Sorry, no matching records found"
        ),
      },
    },
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    dispatch(deleteEmailSchedule(selectedId)).then(() => {
      setCount(count + 1);
      setDeleteDialogOpen(false);
    });
  };

  const handleEdit = (id) => {
    dispatch(getBTemplates());
    const row = data.find((d) => d.id === id);
    if (row?.status === "1") return;

    setEditData({
      schedule_time: convertToTimeZone(
        formatDate(row?.schedule_time),
        currentTZ
      ),
      template_id: row.template_id || "",
      deal_id: row.deal_id,
    });
    setSelectedId(id);
    setSelectedRow(row);
    setEditDialogOpen(true);
  };

  const submitEdit = () => {
    dispatch(updateEmailSchedule(selectedId, editData)).then(() => {
      setCount(count + 1);
      setEditDialogOpen(false);
    });
  };

  return (
    <>
      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          Confirm Delete
          <IconButton
            aria-label="close"
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this schedule?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Email Schedule
          <IconButton
            aria-label="close"
            onClick={() => setEditDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Schedule Time"
              value={editData.schedule_time}
              onChange={(newValue) =>
                setEditData({ ...editData, schedule_time: newValue })
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth margin="normal" />
              )}
              minDateTime={new Date()}
            />
          </LocalizationProvider>
          <FormControl fullWidth sx={{ mt: 1 }}>
            {/* <InputLabel sx={{ mb: 1 }}>Email Template</InputLabel> */}
            <Typography variant="body1" fontWeight="normal" sx={{ mb: 1 }}>
              Select Template
            </Typography>
            <Select
              value={editData.template_id}
              onChange={(e) =>
                setEditData({ ...editData, template_id: e.target.value })
              }
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Email Template
              </MenuItem>
              {bodyTemplates?.map((template) => (
                <MenuItem key={template.tb_id} value={template.tb_id}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={submitEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table */}
      <MUIDataTable
        title={"Deal Email Schedule Logs"}
        data={data}
        columns={columns}
        options={options}
      />
    </>
  );
};

export default EmailSchedule;
